import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchInvoiceOptions, listCustomers, fetchCustomer, updateCustomer, listItems, listSalesmen, fetchFamilyMembers,
  fetchInvoice, createInvoice, updateInvoice, deleteInvoice, createFamilyMember,
  createCustomer, uploadFile, checkInvoiceNo, listMeasurements, fetchCustomerMeasurements, fetchCustomerInvoices,
  fetchPreferences, savePreferences, fetchCompanySettings,
} from '../api/resources';
import MeasurementInput from '../components/ui/MeasurementInput';
import WorkerAllocation from '../components/invoice/WorkerAllocation';
import FamilyMemberField from '../components/invoice/FamilyMemberField';
import OrderTimeline from '../components/invoice/OrderTimeline';
import StageControl from '../components/invoice/StageControl';
import { useAuth } from '../context/AuthContext';
import { ChangeLogHint, ChangeLogModal, groupHistory } from '../components/ui/ChangeLog';
import { parseMeasurement, formatMeasurement } from '../utils/fraction';
import { useToast } from '../context/ToastContext';
import { inr, todayISO, assetUrl } from '../utils/format';
import { computeLine, invoiceTotals } from '../utils/calc';
import { printMeasurementSlip } from '../utils/measurementSlip';
import { groupBillLines } from '../utils/groupLines';
import Field from '../components/ui/Field';
import Modal from '../components/ui/Modal';
import SearchableSelect from '../components/ui/SearchableSelect';
import Spinner from '../components/ui/Spinner';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { Check, ChevronDown, History, Printer, FilePlus2, Lock, Rows2, Rows4, Save, Trash2, X, Pencil, Plus } from 'lucide-react';
import { ZONE_LABEL, ZONE_ORDER } from './portal/shared';
import { forWhom } from '../utils/orderFor';

/*
 * Order screen, modelled on the desktop billing software the shop already
 * knows:
 *
 *   [New] [Modify] [Save] [Delete] [Cancel] [Close]
 *   ORDER HEADER ─ invoice no, date, type, customer, mobile, address, ref, days, delivery, trial
 *   ORDER ITEMS             │  [Tailoring Item] [Invoicing]
 *   PREVIOUS ORDERS/HISTORY │   item, pcs, rate, amt, measurement grid, add to order (F5)
 *   ITEM DETAIL             │
 *   BILLING ─ totals, taxes, adjust, freight, salesmen, bill amount, advance/receipt
 */

// Normal or compact view, remembered per browser; compact unless Normal was picked.
const VIEW_KEY = 'order-view';
const readCompact = () => { try { return localStorage.getItem(VIEW_KEY) !== 'normal'; } catch { return true; } };
const writeCompact = (on) => { try { localStorage.setItem(VIEW_KEY, on ? 'compact' : 'normal'); } catch { /* ignore */ } };

// A half-filled order survives a trip to another page (previous orders, a new
// item, a mis-click) because it is kept in this tab's own memory. It is NOT
// the database: nothing here is a saved order, and pressing Save is still what
// writes it. The draft is dropped the moment the order is saved or thrown away.
const draftKey = (id) => `order-draft:${id || 'new'}`;
const DRAFT_MAX_AGE = 12 * 60 * 60 * 1000; // a day-old draft is stale, not helpful

const readDraft = (id) => {
  try {
    const raw = sessionStorage.getItem(draftKey(id));
    if (!raw) return null;
    const { savedAt, form } = JSON.parse(raw);
    if (!form || !savedAt || Date.now() - savedAt > DRAFT_MAX_AGE) return null;
    return form; // carries __openKey: which garment the panel was on
  } catch { return null; } // private mode, or something unreadable
};
const writeDraft = (id, form) => {
  try { sessionStorage.setItem(draftKey(id), JSON.stringify({ savedAt: Date.now(), form })); } catch { /* not kept — fine */ }
};
const dropDraft = (id) => {
  try { sessionStorage.removeItem(draftKey(id)); } catch { /* ignore */ }
};

let keyCounter = 0;
const k = () => ++keyCounter;

const PAY_TYPES = [['CASH', 'Cash'], ['UPI', 'UPI'], ['CARD', 'Card'], ['PAYTM', 'Paytm'], ['WALLET', 'Wallet']];
const DEFAULT_TIME = '19:00';

// ---- date helpers: the screen works in local 'yyyy-mm-dd' / 'yyyy-mm-ddTHH:MM' ----
const pad = (n) => String(n).padStart(2, '0');
const localDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localDT = (d) => `${localDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const isoToDate = (v) => { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : localDate(d); };
const isoToDT = (v) => { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : localDT(d); };
const fmtDMY = (v) => { const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}-${m[2]}-${m[1]}` : ''; };
const plusDays = (dateStr, days, time) => {
  const d = new Date(`${dateStr}T${time || DEFAULT_TIME}`);
  if (isNaN(d)) return '';
  d.setDate(d.getDate() + Number(days || 0));
  return localDT(d);
};
const dayDiff = (fromDate, toDT) => {
  const a = new Date(`${fromDate}T00:00`);
  const b = new Date(`${String(toDT).slice(0, 10)}T00:00`);
  if (isNaN(a) || isNaN(b)) return '';
  return String(Math.max(0, Math.round((b - a) / 86400000)));
};
const timeOf = (dt) => (dt && dt.length >= 16 ? dt.slice(11, 16) : DEFAULT_TIME);

const EMPTY_FORM = () => ({
  seriesId: '', orderNo: '', orderDate: todayISO(), reference: '',
  customerId: '', customerName: '', customerMobile: '', customerAddress: '', customerPlace: '', customerStreet0: '',
  // Whose clothes these are. One order is for one person, so this sits here
  // and not on the garments.
  forFamily: false, familyMemberId: '', familyMemberName: '', familyMemberRelation: '',
  deliveryDays: '', deliveryDate: '', trialOn: false, trialDate: '', creditDays: '',
  salesman1Id: '', salesman2Id: '', metersUsed: '', adjustAmount: '', freightAmount: '',
  items: [], payments: [],
});

const EMPTY_DRAFT = () => ({
  tailoringItemId: '', itemName: '', qty: 1, rate: '', discountPct: 0, discountAmt: '',
  gstIncl: false, taxRate: '', familyMemberId: '', familyMemberName: '', salesmanId: '',
  salesmanName: '', remarks: '', images: [], patterns: [], handMarks: [], audio: '', styleSelections: [],
  measurements: [], assignments: [], alteration: false, printOnInvoice: true,
});

const addressOf = (c) => [c.addressLine1, c.addressLine2, c.city, c.state, c.pin].filter(Boolean).join(', ');
// The editable part of the address (house / street) and the fixed part (city, state, PIN).
const streetOf = (c) => [c.addressLine1, c.addressLine2].filter(Boolean).join(', ');
const placeOf = (c) => [c.city, c.state, c.pin].filter(Boolean).join(', ');
const customerFields = (c) => ({
  customerId: String(c.id),
  customerName: c.name || '',
  customerMobile: c.mobile || '',
  customerAddress: streetOf(c),
  customerPlace: placeOf(c),
  customerStreet0: streetOf(c), // as loaded — a change is written back to the profile on save
});

export default function InvoiceWizard() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { success, error } = useToast();
  const { role } = useAuth(); // only an admin may override a worker's cost
  const isEdit = !!id;
  // /invoices/new?customer=12 opens a new order for that customer (Customers page).
  const [params] = useSearchParams();
  const presetCustomer = !isEdit ? params.get('customer') : null;
  // Where Close / Cancel / Back lead: the page that opened this screen.
  const location = useLocation();
  const backTo = location.state?.from || '/invoices';
  // Set by the Back button on the customer's orders page.
  const resumeDraft = !!location.state?.resumeDraft;
  const keep = { state: location.state };
  const goBack = () => nav(backTo);
  // What the Back / Close buttons call the place they lead to.
  const backLabel = backTo.startsWith('/customers')
    ? 'Back to Customers'
    : backTo === '/supervisor'
      ? 'Back to Supervisor'
      : backTo === '/production'
        ? 'Back to Production'
        : 'Back to Orders';
  // Leaving for the history page parks the half-filled order and records where
  // to come back to; the Back button there asks for it to be put back.
  const openHistory = () => {
    writeDraft(isEdit ? id : null, { ...form, __openKey: editingKey });
    nav(`/customers/${form.customerId}/orders`, {
      state: { from: location.pathname + location.search, wizardState: location.state || null },
    });
  };

  const [form, setForm] = useState(EMPTY_FORM);
  const [loaded, setLoaded] = useState(false);
  const restored = useRef(false); // a kept draft was put back on screen
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draftPending, setDraftPending] = useState(false);
  // Set by the item panel: hands back the cards waiting to be added.
  const pendingRef = useRef(null);
  const [compact, setCompactState] = useState(readCompact);
  const setCompact = (on) => { setCompactState(on); writeCompact(on); };

  // Compact view makes room: sidebar folded, page at full width. Leaving the
  // order screen (or going back to Normal) opens the sidebar again.
  const shell = useOutletContext();
  useEffect(() => {
    if (!shell) return undefined;
    shell.setCollapsed(compact);
    shell.setWide(compact);
    return () => { shell.setCollapsed(false); shell.setWide(false); };
  }, [compact, shell]);

  const { data: options } = useQuery({ queryKey: ['invoice-options'], queryFn: fetchInvoiceOptions });
  const series = options?.series || [];
  const { data: salesmenData } = useQuery({ queryKey: ['salesmen'], queryFn: listSalesmen });
  const salesmen = salesmenData?.salesmen || [];

  useEffect(() => {
    if (!isEdit && !form.seriesId && series.length) setForm((f) => ({ ...f, seriesId: String(series[0].id) }));
  }, [isEdit, series, form.seriesId]);

  const load = useCallback(async () => {
    if (!isEdit) {
      const fresh = EMPTY_FORM();
      if (presetCustomer) {
        try {
          const { customer: c } = await fetchCustomer(Number(presetCustomer));
          Object.assign(fresh, customerFields(c));
        } catch {
          error('Could not load that customer.');
        }
      }
      // A draft is only put back when the user asked to come back to it
      // (the Back button on the customer's orders page). Arriving from a menu
      // means a new order, so anything kept is dropped.
      const kept = resumeDraft ? readDraft(null) : null;
      if (!resumeDraft) dropDraft(null);
      // Only a draft for this same customer; otherwise it belongs to another order.
      const mine = kept && (!presetCustomer || String(kept.customerId) === String(presetCustomer));
      const { __openKey: openKey, ...keptForm } = kept || {};
      setForm(mine ? keptForm : fresh);
      if (mine) setEditingKey(openKey ?? null);
      if (mine && (kept.items?.length || kept.customerId)) restored.current = true;
      setLoaded(true);
      return;
    }
    try {
      const { invoice } = await fetchInvoice(Number(id));
      const c = invoice.customer || {};
      setForm({
        seriesId: invoice.seriesId ? String(invoice.seriesId) : '',
        orderNo: invoice.orderNo || '',
        orderDate: isoToDate(invoice.orderDate) || todayISO(),
        reference: invoice.reference || '',
        ...(c.id ? customerFields(c) : {}),
        // Who the order was taken for. Only an order with a name on it opens
        // the box — an ordinary order loads with it closed, as it was.
        forFamily: !!invoice.familyMemberId,
        familyMemberId: invoice.familyMemberId ? String(invoice.familyMemberId) : '',
        familyMemberName: invoice.familyMember?.name || '',
        familyMemberRelation: invoice.familyMember?.relation || '',
        deliveryDays: invoice.deliveryDays ?? '',
        deliveryDate: isoToDT(invoice.deliveryDate),
        trialOn: !!invoice.trialDate,
        trialDate: isoToDT(invoice.trialDate),
        creditDays: invoice.creditDays ?? '',
        salesman1Id: invoice.salesman1Id ? String(invoice.salesman1Id) : '',
        salesman2Id: invoice.salesman2Id ? String(invoice.salesman2Id) : '',
        metersUsed: invoice.metersUsed ?? '',
        adjustAmount: invoice.adjustAmount || '',
        freightAmount: invoice.freightAmount || '',
        items: (invoice.items || []).map((it) => ({
          localKey: k(), tailoringItemId: it.tailoringItemId ? String(it.tailoringItemId) : '',
          itemName: it.itemName || '', qty: it.qty, rate: it.rate,
          amount: it.amount, discountPct: it.discountPct, discountAmt: it.discountAmt,
          gstIncl: it.gstIncl, taxRate: it.taxRate, gstAmount: it.gstAmount,
          taxableAmt: it.taxableAmt, finalAmt: it.finalAmt,
          familyMemberId: it.familyMemberId ? String(it.familyMemberId) : '', familyMemberName: it.familyMember?.name || '',
          salesmanId: it.salesmanId ? String(it.salesmanId) : '', salesmanName: it.salesman?.name || '',
          remarks: it.remarks || '', images: it.images || [], patterns: it.patterns || [],
          handMarks: it.handMarks || [], audio: it.audio || '', styleSelections: it.styleSelections || [],
          measurements: it.measurements || [],
          alteration: !!it.alteration, printOnInvoice: it.printOnInvoice !== false,
          // The set this garment came in as, so a reopened order still knows
          // which parts belong together.
          setKey: it.setKey || null, setName: it.setName || null, setItemId: it.setItemId || null,
          stage: it.stage, stageAt: it.stageAt, expectedAt: it.expectedAt, holder: it.holder?.name || null,
          // Kept so the save updates the line in place — recreating it would
          // detach its work assignments.
          id: it.id,
          assignments: (it.assignments || []).map((a) => ({
            workType: a.workType,
            // Which garment of the line this job is for. Dropping it here put
            // every job back on piece 1, so a second one looked like a clash.
            pieceNo: a.pieceNo || 1,
            workerId: String(a.workerId), workerName: a.workerName,
            calculatedCost: a.calculatedCost, overrideCost: a.overrideCost, overrideReason: a.overrideReason || '',
            status: a.status, settled: a.settled, settlementNo: a.settlementNo,
          })),
        })),
        invoiceId: invoice.id,
        payments: (invoice.payments || []).map((p) => ({
          localKey: k(), date: isoToDate(p.date), amount: p.amount, type: p.type, remark: p.remark || '',
        })),
      });
      const kept = resumeDraft ? readDraft(id) : null;
      if (!resumeDraft) dropDraft(id);
      if (kept && String(kept.orderNo) === String(invoice.orderNo)) {
        const { __openKey: openKey, ...keptForm } = kept;
        setForm(keptForm);
        setEditingKey(openKey ?? null);
        restored.current = true;
      } else {
        setEditingKey(null);
      }
    } catch {
      error('Could not load this order.');
    } finally {
      setLoaded(true);
    }
  }, [isEdit, id, error, presetCustomer, resumeDraft]);

  useEffect(() => { load(); }, [load]);

  // Tell the user once that their unsaved work came back.
  useEffect(() => {
    if (loaded && restored.current) {
      restored.current = false;
      success('Your unsaved order was put back. Press Save to store it.');
    }
  }, [loaded, success]);

  // Keep the draft current while the order is being filled in.
  useEffect(() => {
    if (!loaded) return undefined;
    const t = setTimeout(() => {
      if (form.customerId || form.items.length) writeDraft(isEdit ? id : null, { ...form, __openKey: editingKey });
    }, 400);
    return () => clearTimeout(t);
  }, [form, editingKey, loaded, isEdit, id]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Work days ↔ delivery date: whichever was typed drives the other.
  const setDays = (v) => setForm((f) => ({
    ...f, deliveryDays: v, deliveryDate: v === '' ? f.deliveryDate : plusDays(f.orderDate, v, timeOf(f.deliveryDate)),
  }));
  const setDelivery = (v) => setForm((f) => ({ ...f, deliveryDate: v, deliveryDays: v ? dayDiff(f.orderDate, v) : '' }));
  const setOrderDate = (v) => setForm((f) => ({
    ...f, orderDate: v,
    deliveryDate: f.deliveryDays !== '' && v ? plusDays(v, f.deliveryDays, timeOf(f.deliveryDate)) : f.deliveryDate,
  }));
  const setTrialOn = (on) => setForm((f) => {
    if (!on) return { ...f, trialOn: false, trialDate: '' };
    const base = f.deliveryDate || plusDays(f.orderDate, 7);
    const d = new Date(base);
    d.setDate(d.getDate() - 2);
    return { ...f, trialOn: true, trialDate: f.trialDate || localDT(d) };
  });

  // ---- money ----
  const lineTotals = useMemo(() => invoiceTotals(form.items), [form.items]);
  const adjust = Number(form.adjustAmount) || 0;
  const freight = Number(form.freightAmount) || 0;
  const billAmount = Math.round((lineTotals.totalAmount + adjust + freight) * 100) / 100;
  const received = useMemo(() => form.payments.reduce((s, p) => s + (Number(p.amount) || 0), 0), [form.payments]);
  const due = Math.round((billAmount - received) * 100) / 100;

  const saveBlocker = !form.customerId
    ? 'Choose a customer first'
    : (form.items.length === 0 && !draftPending) ? 'Pick at least one garment' : '';

  const saveInvoice = async () => {
    if (saving) return;
    if (saveBlocker) { error(saveBlocker); return; }
    // Cards still in the panel are part of this order: take them along.
    const flushed = pendingRef.current ? pendingRef.current() : [];
    const items = [...form.items, ...flushed];
    setSaving(true);
    const body = {
      seriesId: form.seriesId ? Number(form.seriesId) : undefined,
      orderNo: form.orderNo || undefined,
      orderDate: form.orderDate,
      reference: form.reference,
      customerId: Number(form.customerId),
      // Untick the box and the order goes back to being the customer's own.
      familyMemberId: form.forFamily && form.familyMemberId ? Number(form.familyMemberId) : null,
      deliveryDays: form.deliveryDays !== '' ? Number(form.deliveryDays) : undefined,
      deliveryDate: form.deliveryDate || undefined,
      trialDays: form.trialOn && form.trialDate ? Number(dayDiff(form.orderDate, form.trialDate)) : undefined,
      trialDate: form.trialOn && form.trialDate ? form.trialDate : undefined,
      creditDays: form.creditDays !== '' ? Number(form.creditDays) : undefined,
      salesman1Id: form.salesman1Id || null,
      salesman2Id: form.salesman2Id || null,
      metersUsed: form.metersUsed,
      adjustAmount: adjust,
      freightAmount: freight,
      items: items.map((it) => ({
        id: it.id ?? undefined,
        tailoringItemId: it.tailoringItemId ? Number(it.tailoringItemId) : null,
        itemName: it.itemName,
        qty: Number(it.qty), rate: Number(it.rate),
        discountPct: Number(it.discountPct || 0),
        discountAmt: it.discountAmt != null && it.discountAmt !== '' ? Number(it.discountAmt) : null,
        gstIncl: it.gstIncl, taxRate: Number(it.taxRate || 0),
        familyMemberId: it.familyMemberId ? Number(it.familyMemberId) : null,
        salesmanId: it.salesmanId ? Number(it.salesmanId) : null,
        remarks: it.remarks, images: it.images || [], patterns: it.patterns || [],
        handMarks: it.handMarks || [], audio: it.audio || '', styleSelections: it.styleSelections || [],
        alteration: !!it.alteration, printOnInvoice: it.printOnInvoice !== false,
        // Which set this garment came in as. The screen already split the set
        // into its parts, so the server has nothing left to expand — it just
        // records what they belong to.
        setKey: it.setKey || null,
        setName: it.setName || null,
        setItemId: it.setItemId ? Number(it.setItemId) : null,
        measurements: (it.measurements || [])
          .filter((m) => m.value != null || m.notes)
          .map((m) => ({
            measurementId: Number(m.measurementId),
            value: m.value,
            displayValue: m.displayValue || formatMeasurement(m.value),
            notes: m.notes || '',
          })),
        // Overrides go back exactly as loaded, so a staff save of an order
        // carrying an admin override is not read as changing it.
        assignments: (it.assignments || [])
          .filter((a) => a.workerId)
          .map((a) => ({
            workType: a.workType,
            // The server keys work by piece as well as by type; without this
            // two garments' jobs arrive as two jobs on the same garment.
            pieceNo: a.pieceNo || 1,
            workerId: Number(a.workerId),
            overrideCost: a.overrideCost === '' || a.overrideCost == null ? null : Number(a.overrideCost),
            overrideReason: a.overrideReason || undefined,
            status: a.status || undefined,
          })),
      })),
      payments: form.payments
        .filter((p) => Number(p.amount) > 0)
        .map((p) => ({ date: p.date, amount: Number(p.amount), type: p.type, remark: p.remark })),
    };
    try {
      const res = isEdit ? await updateInvoice(Number(id), body) : await createInvoice(body);
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['customer-measurements'] });
      qc.invalidateQueries({ queryKey: ['order-history'] });
      success(res.message);
      const street = form.customerAddress.trim();
      if (street !== (form.customerStreet0 || '').trim()) {
        try {
          await updateCustomer(Number(form.customerId), { addressLine1: street, addressLine2: null });
          setForm((f) => ({ ...f, customerStreet0: street }));
          qc.invalidateQueries({ queryKey: ['customers'] });
          qc.invalidateQueries({ queryKey: ['wizard-customers'] });
        } catch (x) {
          error(x.response?.data?.message || 'Order saved, but the address could not be updated on the customer.');
        }
      }
      dropDraft(isEdit ? id : null); // saved — the draft has done its job
      setCreated({ id: res.invoice?.id ?? Number(id), orderNo: res.invoice?.orderNo || form.orderNo });
    } catch (x) {
      error(x.response?.data?.message || 'Could not save the order.');
    } finally {
      setSaving(false);
    }
  };

  const removeOrder = async () => {
    try {
      const res = await deleteInvoice(Number(id));
      dropDraft(id);
      qc.invalidateQueries({ queryKey: ['invoices'] });
      success(res.message || 'Order moved to the trash.');
      goBack();
    } catch (x) {
      error(x.response?.data?.message || 'Could not move the order to the trash.');
    } finally {
      setConfirmDelete(false);
    }
  };

  const startNew = () => {
    dropDraft(null);
    if (isEdit || presetCustomer) { nav('/invoices/new', keep); return; }
    setForm({ ...EMPTY_FORM(), seriesId: series[0] ? String(series[0].id) : '' });
    setEditingKey(null);
  };
  const cancel = () => {
    if (!window.confirm('Throw away the changes on this screen?')) return;
    dropDraft(isEdit ? id : null);
    if (backTo !== '/invoices') goBack();
    else if (isEdit) load();
    else startNew();
  };

  // Ctrl+S saves the order.
  const saveRef = useRef(saveInvoice);
  saveRef.current = saveInvoice;
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); saveRef.current(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!loaded) return <Spinner label="Loading order…" />;

  const editingLine = form.items.find((x) => x.localKey === editingKey) || null;
  // The panel holds picked items as cards until they are added (F5 or Save),
  // so several garments can be measured before anything lands on the order.
  // One garment, one line.
  //
  // "Kurta x 5" is five garments on the floor: five stages, five trials, five
  // tailors possibly. All of that already hangs off a line, so five garments
  // have to be five lines — otherwise the shop can only ever tick all five at
  // once, which is the thing it cannot do.
  //
  // The bill still reads "Kurta x 5": identical lines are added back together
  // when it is printed. Orders taken before this rule keep whatever quantity
  // they were saved with; nothing old is rewritten.
  const appendLines = (lines) => {
    if (!lines?.length) return [];
    const withKeys = lines.flatMap((l) => {
      const copies = Math.max(1, Math.trunc(Number(l.qty) || 1));
      if (copies === 1) return [{ ...l, localKey: k() }];
      // The money on the line was worked out for the whole quantity, so each
      // garment has to be priced again for one — otherwise two trousers at
      // ₹400 each would both read ₹800.
      const one = { ...l, qty: 1 };
      const priced = { ...one, ...computeLine(one) };
      return Array.from({ length: copies }, () => ({ ...priced, localKey: k() }));
    });
    setForm((f) => ({ ...f, items: [...f.items, ...withKeys] }));
    return withKeys;
  };
  // Live edits of the line the panel is on.
  const updateLine = (line) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((x) => (x.localKey === editingKey ? { ...line, localKey: editingKey, id: x.id } : x)),
    }));
  };

  return (
    <div className={`${compact ? 'dense' : 'max-w-[96rem]'} of-stack mx-auto space-y-4`}>
      {/* ---- command bar ---- */}
      <div className="of-toolbar sticky top-0 z-20 flex flex-wrap items-center gap-1.5 rounded-lg border border-line bg-surface px-2 py-2 shadow-sm sm:px-3">
        <button className="btn-primary" onClick={startNew}><FilePlus2 size={15} /> New</button>
        <button className="btn-primary" disabled={saving} onClick={saveInvoice} title={saveBlocker || 'Save (Ctrl+S)'}>
          <Save size={15} /> {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="btn-outline text-danger-600" disabled={!isEdit} onClick={() => setConfirmDelete(true)} title="Move this order to the trash — it can be restored">
          <Trash2 size={15} /> Trash
        </button>
        <button className="btn-outline" onClick={cancel}>Cancel</button>
        <button className="btn-outline" onClick={goBack} title={backLabel}><X size={15} /> Close</button>

        {/* The Normal/Compact switch is about fitting more on a wide screen;
            on a phone the layout is single-column either way. */}
        <div className="ml-2 hidden rounded-md border border-line bg-surface-sunken p-0.5 lg:inline-flex" role="group" aria-label="View">
          {[[false, 'Normal', Rows2], [true, 'Compact', Rows4]].map(([value, label, Icon]) => (
            <button
              key={label}
              type="button"
              aria-pressed={compact === value}
              onClick={() => setCompact(value)}
              className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold ${compact === value ? 'bg-surface text-brand-700 shadow-xs' : 'text-content-muted hover:text-content'}`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {form.customerId && <PreviousOrdersButton customerId={form.customerId} onOpen={openHistory} />}

        <p className="ml-auto text-xs text-content-subtle">
          {isEdit ? <b className="text-content">Editing order {form.orderNo}</b> : <b className="text-content">New order</b>}
          {/* The keyboard tips are for the shop's desk machines; a phone has
              no F5, so they only take up room there. */}
          <span className="hidden lg:inline">
            {' · '}Tip: press <b>F5</b> inside the item panel to add the item · <b>Ctrl+S</b> saves
          </span>
        </p>
      </div>

      {/* ---- ORDER HEADER ---- */}
      <Panel title="Order Header" tone="brand">
        <OrderHeader
          form={form}
          set={set}
          isEdit={isEdit}
          id={id}
          setOrderDate={setOrderDate}
          setDays={setDays}
          setDelivery={setDelivery}
          setTrialOn={setTrialOn}
        />
      </Panel>

      {isEdit && <TimelinePanel invoiceId={Number(id)} />}

      {/* ---- picking a garment (left) | measuring it (right) ---- */}
      <ItemEditor
        key={editingKey ?? 'new'}
        form={form}
        line={editingLine}
        disabled={!form.customerId}
        onAppend={appendLines}
        flushRef={pendingRef}
        onLiveChange={updateLine}
        onCancelEdit={() => setEditingKey(null)}
        onPendingChange={setDraftPending}
        items={form.items}
        money={{ totals: lineTotals, billAmount, received, due, salesmen }}
        leftExtras={(
          <div className="of-stack min-w-0 space-y-3">
            <Panel title="Order Items" tone="accent" badge={`${form.items.length} item${form.items.length === 1 ? '' : 's'}`}>
            <ItemsTable
              form={form}
              items={form.items}
              onStageMoved={(localKey, patch) => setForm((f) => ({
                ...f,
                items: f.items.map((x) => (x.localKey === localKey ? { ...x, ...patch } : x)),
              }))}
              editingKey={editingKey}
              onEdit={setEditingKey}
              onRemove={(key) => {
                // A set is sold whole, so removing one part takes the rest
                // with it — a Coat Pant cannot become a lone coat.
                const line = form.items.find((x) => x.localKey === key);
                const going = line?.setKey
                  ? form.items.filter((x) => x.setKey === line.setKey).map((x) => x.localKey)
                  : [key];
                if (going.includes(editingKey)) setEditingKey(null);
                set({ items: form.items.filter((x) => !going.includes(x.localKey)) });
              }}
            />
            </Panel>
            <Panel title="Worker Allocation" tone="success" badge={form.items.length ? undefined : 'add a garment first'}>
              <WorkerAllocation
                items={form.items}
                canOverride={role === 'ADMIN'}
                onChangeLine={(next) => setForm((f) => ({
                  ...f,
                  items: f.items.map((x) => (x.localKey === next.localKey ? next : x)),
                }))}
              />
            </Panel>
          </div>
        )}
      />

      {/* ---- BILLING ---- */}
      <Panel title="Billing" tone="warning">
        <Billing
          form={form}
          set={set}
          salesmen={salesmen}
          totals={lineTotals}
          billAmount={billAmount}
          received={received}
          due={due}
        />
      </Panel>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Move Order to Trash"
        message={`Move order ${form.orderNo} to the trash? It comes off the order list and out of the reports, and an admin or supervisor can restore it from Orders → Trash. Nothing is deleted.`}
        onConfirm={removeOrder}
      />

      <Modal open={!!created} onClose={goBack} title={isEdit ? 'Order Saved' : 'Order Created'} size="sm">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-success-50 text-success-600">
            <Check size={30} />
          </div>
          <p className="text-base font-semibold text-content">Order <b>{created?.orderNo}</b> is saved.</p>
          <p className="mt-1 text-sm text-content-muted">Print it now?</p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <button className="btn-outline" onClick={goBack}>{backLabel}</button>
            {!isEdit && (
              <button className="btn-outline" onClick={() => { setCreated(null); startNew(); }}>New Order</button>
            )}
            <button className="btn-primary" onClick={() => created && nav(`/invoices/${created.id}`)}>Print</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ---------------- building blocks ---------------- */
// Each section has its own colour so the eye can tell them apart at a glance.
// Full class strings, so Tailwind keeps them.
const TONES = {
  brand: { box: 'border-brand-300', head: 'bg-brand-700 text-white', badge: 'bg-white/20 text-white' },
  accent: { box: 'border-accent-400', head: 'bg-accent-600 text-white', badge: 'bg-white/20 text-white' },
  slate: { box: 'border-slate-400', head: 'bg-slate-600 text-white', badge: 'bg-white/20 text-white' },
  success: { box: 'border-success-500', head: 'bg-success-600 text-white', badge: 'bg-white/20 text-white' },
  warning: { box: 'border-warning-500', head: 'bg-warning-600 text-white', badge: 'bg-white/20 text-white' },
};

// A section. Pass `open`/`onToggle` to make it foldable: the coloured header
// stays put as the way to bring it back, so nothing disappears for good.
function Panel({ title, badge, right, children, className = '', tone = 'brand', open, onToggle }) {
  const t = TONES[tone];
  const foldable = typeof onToggle === 'function';
  const shown = !foldable || open;
  return (
    <section className={`rounded-lg border-2 bg-surface shadow-sm ${t.box} ${className}`}>
      <header className={`of-step-head flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 ${t.head} ${shown ? 'rounded-t-[5px]' : 'rounded-[5px]'}`}>
        {foldable ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!!open}
            className="-mx-1 flex min-w-0 flex-1 items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-white/10"
            title={open ? 'Hide this section' : 'Show this section'}
          >
            <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
            <h2 className="shrink-0 text-xs font-bold uppercase tracking-wider">{title}</h2>
            {/* The badge only explains the section; on a phone it would crowd
                out the title it is explaining, so it waits for the room. */}
            {badge && <span className={`hidden truncate rounded-full px-2 py-0.5 text-2xs font-bold uppercase sm:inline ${t.badge}`}>{badge}</span>}
            <span className="ml-auto rounded px-1.5 py-0.5 text-2xs font-bold uppercase tracking-wider opacity-90 hover:bg-white/15">
              {open ? 'Hide' : 'Show'}
            </span>
          </button>
        ) : (
          <>
            <h2 className="shrink-0 text-xs font-bold uppercase tracking-wider">{title}</h2>
            {badge && <span className={`min-w-0 truncate rounded-full px-2 py-0.5 text-2xs font-bold uppercase ${t.badge}`}>{badge}</span>}
          </>
        )}
        {right && <div className={foldable ? 'shrink-0' : 'ml-auto'}>{right}</div>}
      </header>
      {shown && <div className="of-step-body p-2 sm:p-3">{children}</div>}
    </section>
  );
}

function L({ children, className = '' }) {
  return <span className={`of-label mb-1 block text-xs font-semibold text-content-muted ${className}`}>{children}</span>;
}

/* ---------------- ORDER HEADER ---------------- */
function useGstOn() {
  const { companyId, currentCompany } = useAuth();
  const { data } = useQuery({
    queryKey: ['company-settings', companyId],
    queryFn: () => fetchCompanySettings(companyId),
    enabled: !!companyId,
  });
  const company = data?.company || currentCompany?.company;
  return !!company?.gstEnabled;
}

/**
 * The invoice number can be typed over — a shop often continues a book it
 * already keeps. Two orders must not share one, so the number is checked while
 * it is typed and again on save, where it actually matters.
 */
function InvoiceNoField({ value, onChange, invoiceId }) {
  const [state, setState] = useState({ checking: false, taken: false, checked: '' });

  useEffect(() => {
    const no = String(value || '').trim();
    if (!no) { setState({ checking: false, taken: false, checked: '' }); return undefined; }
    setState((s) => ({ ...s, checking: true }));
    const t = setTimeout(async () => {
      try {
        const r = await checkInvoiceNo(no, invoiceId || undefined);
        setState({ checking: false, taken: !!r.taken, checked: no });
      } catch {
        setState({ checking: false, taken: false, checked: no });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [value, invoiceId]);

  const taken = state.taken && state.checked === String(value || '').trim();
  return (
    <>
      <input
        className={`input ${taken ? 'border-danger-500 ring-1 ring-danger-500' : ''}`}
        value={value}
        placeholder="Auto"
        title="Leave it blank for the next number in the series, or type your own"
        onChange={(e) => onChange(e.target.value)}
      />
      {taken && <span className="mt-0.5 block text-2xs font-semibold text-danger-600">Already used — pick another</span>}
      {!taken && state.checking && <span className="mt-0.5 block text-2xs text-content-subtle">checking…</span>}
    </>
  );
}

function OrderHeader({ form, set, isEdit, id, setOrderDate, setDays, setDelivery, setTrialOn }) {
  const qc = useQueryClient();
  const [custQuery, setCustQuery] = useState('');
  const [quickAdd, setQuickAdd] = useState(null); // text typed when Enter found no match
  const { data: custData, isLoading } = useQuery({
    queryKey: ['wizard-customers', custQuery],
    queryFn: () => listCustomers({ search: custQuery, perPage: 100 }),
  });
  const options = (custData?.customers || []).map((c) => ({
    id: c.id,
    label: c.name,
    mobile: c.mobile,
    raw: c,
    sub: `📞 ${c.mobile || '—'}${addressOf(c) ? ` · 📍 ${addressOf(c)}` : ''}`,
  }));
  // A different customer means a different family, so the person the order
  // is for cannot survive the change.
  const pick = (o) => set({
    ...customerFields(o.raw || o),
    forFamily: false, familyMemberId: '', familyMemberName: '', familyMemberRelation: '',
  });

  return (
    <div className="of-when grid grid-cols-2 gap-x-3 gap-y-2 md:grid-cols-6 xl:grid-cols-12">
      <label className="block xl:col-span-1">
        <L>Invoice No.</L>
        <InvoiceNoField value={form.orderNo} onChange={(orderNo) => set({ orderNo })} invoiceId={isEdit ? id : null} />
      </label>
      <label className="block xl:col-span-2">
        <L>Date</L>
        <input type="date" className="input" value={form.orderDate} onChange={(e) => setOrderDate(e.target.value)} />
      </label>
      <div className="col-span-2 md:col-span-4 xl:col-span-7">
        <L>Customer <span className="text-danger-600">*</span></L>
        <SearchableSelect
          options={options}
          value={form.customerId}
          onSelect={pick}
          onSearch={setCustQuery}
          loading={isLoading}
          selectedLabel={form.customerName ? `${form.customerName}${form.customerMobile ? ' · ' + form.customerMobile : ''}` : ''}
          placeholder="Type name or mobile — Enter adds a new customer"
          searchable
          allowNew
          onNew={(text) => setQuickAdd(text)}
        />
      </div>
      <label className="block md:col-span-2 xl:col-span-2">
        <L>Mobile</L>
        <input className="input bg-surface-sunken" value={form.customerMobile} readOnly tabIndex={-1} />
      </label>

      {/* Who the clothes are for. An order is for one person — a father
          ordering for his son books a second order for himself — so this is
          asked once, here, rather than on every garment. */}
      <FamilyMemberField form={form} set={set} label={L} />

      <label className="col-span-2 block md:col-span-3 xl:col-span-4">
        <L>Address</L>
        <input
          className="input"
          value={form.customerAddress}
          disabled={!form.customerId}
          placeholder={form.customerId ? 'House no., street, area' : 'Choose a customer first'}
          title={form.customerPlace ? `City / State / PIN: ${form.customerPlace}` : undefined}
          onChange={(e) => set({ customerAddress: e.target.value })}
        />
        {form.customerPlace && <span className="mt-0.5 block truncate text-2xs text-content-subtle">{form.customerPlace}</span>}
      </label>
      <label className="block xl:col-span-2">
        <L>Reference</L>
        <input className="input" value={form.reference} placeholder="Referred by" onChange={(e) => set({ reference: e.target.value })} />
      </label>
      <label className="block xl:col-span-2">
        <L>Work Days</L>
        <input type="number" min={0} className="input" value={form.deliveryDays} onChange={(e) => setDays(e.target.value)} />
      </label>
      <div className="xl:col-span-2">
        <label className="of-label mb-1 flex items-center gap-1.5 text-xs font-semibold text-content-muted">
          <input type="checkbox" className="h-3.5 w-3.5 text-brand-700" checked={form.trialOn} onChange={(e) => setTrialOn(e.target.checked)} />
          Trial
        </label>
        <input
          type="datetime-local"
          className="input"
          value={form.trialDate}
          disabled={!form.trialOn}
          onChange={(e) => set({ trialDate: e.target.value })}
        />
      </div>
      <label className="block xl:col-span-2">
        <L>Delivery</L>
        <input type="datetime-local" className="input" value={form.deliveryDate} onChange={(e) => setDelivery(e.target.value)} />
      </label>

      <QuickAddCustomer
        open={quickAdd != null}
        initial={quickAdd || ''}
        onClose={() => setQuickAdd(null)}
        onCreated={(c) => {
          pick(c);
          setQuickAdd(null);
          setCustQuery('');
          qc.invalidateQueries({ queryKey: ['wizard-customers'] });
          qc.invalidateQueries({ queryKey: ['customers'] });
        }}
      />
    </div>
  );
}

/* ---------------- ORDER ITEMS ---------------- */
function ItemsTable({ form, items, editingKey, onEdit, onRemove, onStageMoved }) {
  // The shop's own details for the printed slip. The login only carries the
  // name and GSTIN, so the address and phone come from company settings.
  const { currentCompany, companyId } = useAuth();
  const { data: settings } = useQuery({
    queryKey: ['company-settings', companyId],
    queryFn: () => fetchCompanySettings(companyId),
    enabled: !!companyId,
  });
  const company = settings?.company || currentCompany?.company || null;
  const { error } = useToast();
  // Lines loaded for edit carry measurement ids only, so resolve names here.
  const { data: measData } = useQuery({ queryKey: ['measurements', ''], queryFn: () => listMeasurements('') });
  const metricName = (m) => m.name
    || (measData?.measurements || []).find((x) => x.id === Number(m.measurementId))?.name
    || '';

  const slip = (line) => {
    const opened = printMeasurementSlip({ company, order: form, line, metricName });
    if (!opened) error('Your browser blocked the print window. Allow pop-ups for this site and try again.');
  };
  return (
    // The stage list drops out of the table, so no vertical scroll box here —
    // it would clip it. Sideways is safe, and a seven-column table needs it on
    // a narrow screen.
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full min-w-[42rem] text-sm">
        <thead className="bg-surface-sunken text-left text-2xs font-bold uppercase tracking-wide text-content-muted">
          <tr>
            <th className="px-2 py-1.5">Item Name</th>
            <th className="px-2 py-1.5 text-right">Rate</th>
            <th className="px-2 py-1.5 text-center">Qty</th>
            <th className="px-2 py-1.5 text-right">Amt</th>
            <th className="px-2 py-1.5 text-right">Disc%</th>
            <th className="px-2 py-1.5">Stage</th>
            <th className="w-32 px-2 py-1.5 text-right">Print</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-2 py-6 text-center text-xs italic text-content-subtle">
                No items added yet — pick an item above and click “Add to Order”.
              </td>
            </tr>
          ) : items.map((it) => {
            // Removing any part of a set removes all of them, so a set is
            // locked as soon as settled work sits on any one of its parts.
            const settledOn = (x) => (x.assignments || []).some((a) => a.settled);
            const settled = it.setKey
              ? items.some((x) => x.setKey === it.setKey && settledOn(x))
              : settledOn(it);
            const active = it.localKey === editingKey;
            return (
              <tr
                key={it.localKey}
                onClick={() => onEdit(it.localKey)}
                className={`of-row cursor-pointer border-t border-line ${active ? 'bg-warning-50' : 'hover:bg-surface-hover'}`}
                title="Click to change"
              >
                <td className="px-2 py-1.5 font-semibold text-content">{it.itemName}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{inr(it.rate)}</td>
                <td className="px-2 py-1.5 text-center tabular-nums">{it.qty}</td>
                <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{inr(it.taxableAmt)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{Number(it.discountPct || 0)}</td>
                <td className="px-2 py-1.5 text-xs" onClick={(e) => e.stopPropagation()}>
                  {it.id ? (
                    <StageControl
                      item={it}
                      invoiceId={form.invoiceId}
                      compact
                      onMoved={(patch) => onStageMoved(it.localKey, patch)}
                    />
                  ) : it.alteration ? (
                    <span className="rounded bg-warning-50 px-1.5 py-0.5 font-semibold text-warning-700">Alteration</span>
                  ) : (
                    <span className="text-content-subtle">saves as “Order received”</span>
                  )}
                </td>
                <td className="px-1 py-1 text-right whitespace-nowrap">
                  {/* Named, not just an icon: the shop prints this all day. */}
                  <button
                    className="mr-1 inline-flex items-center gap-1 rounded-md border border-brand-300 bg-brand-50 px-2 py-1 text-2xs font-bold uppercase tracking-wide text-brand-700 hover:bg-brand-600 hover:text-white"
                    title="Print the measurement slip for this garment"
                    onClick={(e) => { e.stopPropagation(); slip(it); }}
                  >
                    <Printer size={13} /> Slip
                  </button>
                  <button className="btn-icon" title="Change" onClick={(e) => { e.stopPropagation(); onEdit(it.localKey); }}>
                    <Pencil size={14} />
                  </button>
                  {settled ? (
                    <span className="btn-icon cursor-not-allowed" title="Worker pay settled — cannot be removed" onClick={(e) => e.stopPropagation()}>
                      <Lock size={14} />
                    </span>
                  ) : (
                    <button
                      className="btn-icon hover:bg-danger-50 hover:text-danger-600"
                      title={it.setKey ? `Remove the whole ${it.setName || 'set'}` : 'Remove'}
                      onClick={(e) => {
                        e.stopPropagation();
                        const ask = it.setKey
                          ? `“${it.setName || it.itemName}” is sold as a set. Remove all of it from this order?`
                          : `Remove ${it.itemName} from this order?`;
                        if (window.confirm(ask)) onRemove(it.localKey);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- ORDER TIMELINE (foldable) ---------------- */
function TimelinePanel({ invoiceId }) {
  const qc = useQueryClient();
  const { data: prefs } = useQuery({ queryKey: ['preferences'], queryFn: fetchPreferences });
  const open = !!prefs?.preferences?.showOrderTimeline;
  const toggle = useMutation({
    mutationFn: (v) => savePreferences({ showOrderTimeline: v }),
    onSuccess: (d) => qc.setQueryData(['preferences'], d),
  });

  return (
    <Panel
      title="Order Timeline"
      tone="slate"
      badge={open ? 'where it is, and where it waited' : undefined}
      open={open}
      onToggle={() => toggle.mutate(!open)}
    >
      {/* Only fetched once it is open. */}
      {open && <OrderTimeline invoiceId={invoiceId} />}
    </Panel>
  );
}

/* ---------------- PREVIOUS ORDERS (opens its own page) ---------------- */
// Only offered when there is something to look at, and opened in a new tab so
// a half-filled order is never lost to a stray click.
function PreviousOrdersButton({ customerId, onOpen }) {
  const { data, isLoading } = useQuery({
    queryKey: ['customer-invoices', customerId],
    queryFn: () => fetchCustomerInvoices(customerId),
    enabled: !!customerId,
  });
  const count = data?.invoices?.length || 0;

  if (isLoading) return <span className="text-2xs text-content-subtle">Checking history…</span>;
  if (!count) return <span className="text-2xs text-content-subtle">No previous orders</span>;

  return (
    <button
      type="button"
      className="btn-outline btn-sm"
      title="See every order this customer has placed"
      onClick={onOpen}
    >
      <History size={13} /> Previous orders ({count})
    </button>
  );
}

/* ---------------- ITEM PANEL: Tailoring Item | Invoicing ---------------- */
function ItemEditor({ form, line, disabled, onAppend, flushRef, onLiveChange, onCancelEdit, onPendingChange, items: orderItems, money, leftExtras }) {
  const { success, error } = useToast();
  const { role } = useAuth();
  const editing = !!line;
  const [draft, setDraft] = useState(() => (line ? { ...EMPTY_DRAFT(), ...line } : EMPTY_DRAFT()));
  // Garments picked but not yet added: small cards under the item field.
  const [cards, setCards] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  // true while the field is empty on purpose, waiting for the next garment
  const [adding, setAdding] = useState(false);
  const gstOn = useGstOn();
  const itemFieldRef = useRef(null);
  const [tab, setTab] = useState('item');
  const [moreOpen, setMoreOpen] = useState(false);
  const [imagesOpen, setImagesOpen] = useState(false);
  const [logFor, setLogFor] = useState(null);
  const settled = ((line || draft).assignments || []).some((a) => a.settled);

  const [itemQuery, setItemQuery] = useState('');
  const { data: itemData, isLoading: itemLoading } = useQuery({
    queryKey: ['wizard-items', itemQuery],
    queryFn: () => listItems({ search: itemQuery, perPage: 100 }),
  });
  const catalogue = itemData?.tailoringItems || [];
  const { data: salesmenData } = useQuery({ queryKey: ['salesmen'], queryFn: listSalesmen });
  const salesmen = salesmenData?.salesmen || [];
  const { data: famData } = useQuery({
    queryKey: ['wizard-family', form.customerId],
    queryFn: () => fetchFamilyMembers(form.customerId),
    enabled: !!form.customerId,
  });
  const family = famData?.familyMembers || [];
  // The numbers a new garment starts from belong to whoever the order is for,
  // so the person is part of the key: tick the box and the panel reloads with
  // that son's measurements instead of his father's.
  const measureFor = form.forFamily && form.familyMemberId ? form.familyMemberId : null;
  const { data: custMeas } = useQuery({
    queryKey: ['customer-measurements', form.customerId, measureFor],
    queryFn: () => fetchCustomerMeasurements(form.customerId, measureFor),
    enabled: !!form.customerId,
  });
  const historyById = useMemo(() => groupHistory(custMeas?.history || []), [custMeas]);
  const { data: measData } = useQuery({ queryKey: ['measurements', ''], queryFn: () => listMeasurements('') });
  const metrics = measData?.measurements || [];

  const savedRows = () => (custMeas?.measurements || [])
    .map((r) => ({ measurementId: Number(r.measurementId), value: parseMeasurement(r.value), notes: '' }))
    .filter((r) => r.value != null);

  // A new garment never starts blank: it takes the numbers from the garment
  // measured just before it, and failing that the customer's saved ones. Editing
  // them here changes only this garment.
  const seedMeasurements = () => {
    const last = cards[cards.length - 1];
    const from = last?.measurements?.length ? last.measurements : savedRows();
    return from.map((m) => ({ ...m }));
  };

  // The wizard asks for these when Save is pressed.
  useEffect(() => { onPendingChange(cards.length > 0); }, [cards.length, onPendingChange]);

  // Every change flows to the order line the panel is on. Debounced, so typing
  // a measurement does not re-render the whole page on each keystroke.
  const firstSync = useRef(true);
  useEffect(() => {
    if (!editing) return undefined;
    if (firstSync.current) { firstSync.current = false; return undefined; }
    const t = setTimeout(() => onLiveChange({ ...draft, ...computeLine(draft) }), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, editing]);

  // What the panel is editing: the order line when one is open, otherwise the
  // card that is selected.
  const card = editing ? null : cards.find((c) => c.localKey === activeCard) || null;
  const current = editing ? draft : (card || EMPTY_DRAFT());
  const calc = computeLine(current);

  const patch = (p) => {
    if (editing) { setDraft((d) => ({ ...d, ...p })); return; }
    if (!card) return;
    setCards((list) => list.map((c) => (c.localKey === card.localKey ? { ...c, ...p } : c)));
  };

  const setMeasurement = (measurementId, p) => {
    const apply = (d) => {
      const rest = d.measurements.filter((m) => Number(m.measurementId) !== Number(measurementId));
      const next = {
        ...(d.measurements.find((m) => Number(m.measurementId) === Number(measurementId)) || {}),
        measurementId: Number(measurementId),
        ...p,
      };
      if (next.value == null && !next.notes) return { ...d, measurements: rest };
      return { ...d, measurements: [...rest, next] };
    };
    if (editing) { setDraft(apply); return; }
    if (!card) return;
    setCards((list) => list.map((c) => (c.localKey === card.localKey ? apply(c) : c)));
  };
  const rowFor = (mid) => current.measurements.find((m) => Number(m.measurementId) === Number(mid));

  const known = new Set(metrics.map((m) => m.id));
  const orphans = current.measurements
    .filter((m) => !known.has(Number(m.measurementId)))
    .map((m) => ({ id: Number(m.measurementId), name: m.name || `Metric #${m.measurementId}`, zone: 'FULL' }));
  const rows = ZONE_ORDER.flatMap((z) => {
    const list = [...metrics, ...orphans].filter((m) => (m.zone || 'FULL') === z);
    return list.length ? [{ zone: z }, ...list] : [];
  });

  // Picking an item adds a card under the field and opens it for measuring.
  const pickItem = (o) => {
    const it = catalogue.find((i) => i.id === o.id);
    if (!it) return;
    if (editing) onCancelEdit(); // leave the order line alone

    // A card is open and you are not adding: you meant to change this one's
    // garment, so keep the measurements already taken on it.
    if (!editing && activeCard && !adding) {
      setCards((list) => list.map((c) => (c.localKey === activeCard
        ? {
          ...c,
          tailoringItemId: String(it.id),
          itemName: it.name,
          rate: it.customerRate != null ? Number(it.customerRate) : 0,
          taxRate: gstOn ? Number(it.taxRate) : 0,
        }
        : c)));
      setTab('item');
      return;
    }

    // A set is one thing on the price list and several garments on the floor,
    // so picking one puts a card out for each part. They are measured, priced
    // and handed out separately from here on — the only thing they share is
    // the set's name, which the bill uses to add them back up and which keeps
    // them from being taken off the order one at a time.
    const parts = it.subItems || [];
    if (parts.length) {
      const setKey = `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
      const cardsForSet = parts.map((c, n) => ({
        ...EMPTY_DRAFT(),
        localKey: `c${Date.now()}-${n}-${Math.random().toString(36).slice(2, 5)}`,
        tailoringItemId: String(c.id),
        // "Coat Pant | Pant" — what the garment is, and what it belongs to.
        itemName: `${it.name} | ${c.name}`,
        rate: c.customerRate != null ? Number(c.customerRate) : 0,
        taxRate: gstOn ? Number(c.taxRate || 0) : 0,
        measurements: seedMeasurements(),
        setKey,
        setName: it.name,
        setItemId: it.id,
      }));
      setCards((list) => [...list, ...cardsForSet]);
      setActiveCard(cardsForSet[0].localKey);
      setAdding(false);
      setTab('item');
      return;
    }

    const fresh = {
      ...EMPTY_DRAFT(),
      localKey: `c${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      tailoringItemId: String(it.id),
      itemName: it.name,
      rate: it.customerRate != null ? Number(it.customerRate) : 0,
      taxRate: gstOn ? Number(it.taxRate) : 0,
      measurements: seedMeasurements(),
    };
    setCards((list) => [...list, fresh]);
    setActiveCard(fresh.localKey);
    setAdding(false);
    setTab('item');
  };

  // Clear the field and wait for the next garment.
  const startAnother = () => {
    setActiveCard(null);
    setAdding(true);
    setTab('item');
    setTimeout(() => itemFieldRef.current?.querySelector('input')?.focus(), 0);
  };

  // A set goes as a set. You cannot drop the Pant out of a Coat Pant and
  // leave a coat priced as a set, so removing one part removes all of them.
  const dropCard = (key) => {
    setCards((list) => {
      const card = list.find((c) => c.localKey === key);
      const going = card?.setKey
        ? list.filter((c) => c.setKey === card.setKey).map((c) => c.localKey)
        : [key];
      setActiveCard((a) => (going.includes(a) ? null : a));
      return list.filter((c) => !going.includes(c.localKey));
    });
  };

  /**
   * Move the cards onto the order. Returns what was added, so Save can include
   * them in the same request rather than waiting for a re-render.
   */
  const commitCards = () => {
    if (!cards.length) return [];
    const lines = cards.map(({ localKey, ...c }) => ({ ...c, ...computeLine(c) }));
    onAppend(lines);
    setCards([]);
    setActiveCard(null);
    return lines;
  };
  if (flushRef) flushRef.current = commitCards;

  const commit = () => {
    if (editing) { onLiveChange({ ...draft, ...calc }); onCancelEdit(); return; }
    const added = commitCards();
    if (added.length) success(`${added.length} garment${added.length === 1 ? '' : 's'} added to the order.`);
  };

  const quickAddFamily = async (name) => {
    try {
      const res = await createFamilyMember(form.customerId, { name, relation: '' });
      patch({ familyMemberId: String(res.familyMember.id), familyMemberName: name });
      success('Family member added.');
    } catch (x) { error(x.response?.data?.message || 'Could not add family member.'); }
  };

  // F5 adds the garment, from anywhere on this screen. The listener is on the
  // window and captures, so the browser never gets the key and never reloads.
  const commitRef = useRef(commit);
  commitRef.current = commit;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'F5') return;
      e.preventDefault();
      e.stopPropagation();
      commitRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const onKeyDown = () => {};

  // Running split of the order by kind.
  const others = orderItems.filter((x) => x.localKey !== line?.localKey);
  const sumOf = (list) => list.reduce((s, x) => s + (Number(x.taxableAmt) || 0), 0);
  const withDraft = editing
    ? [...others, { ...draft, ...calc }]
    : [...others, ...cards.map((c) => ({ ...c, ...computeLine(c) }))];
  const tailoringTotal = sumOf(withDraft.filter((x) => !x.alteration));
  const alterationTotal = sumOf(withDraft.filter((x) => x.alteration));

  let firstField = true;

  return (
    <section
      className={`rounded-lg border-2 bg-surface shadow-sm ${editing ? 'border-warning-500 ring-2 ring-warning-500' : 'border-success-500'}`}
      onKeyDown={onKeyDown}
    >
      {/* The two tabs keep their place and the "which garment" note takes a
          second line when there is no room beside them — a long item name used
          to push the tabs off the edge of a phone. */}
      <div className="flex flex-wrap items-center gap-x-1 gap-y-1 rounded-t-[5px] bg-success-600 px-2 pt-1.5">
        <span className="-mb-px shrink-0 rounded-t-md bg-surface px-3 py-1.5 text-xs font-bold text-success-700">
          Tailoring Item
        </span>
        <button
          type="button"
          onClick={() => setTab(tab === 'billing' ? 'item' : 'billing')}
          className={`-mb-px shrink-0 rounded-t-md px-3 py-1.5 text-xs font-bold ${tab === 'billing' ? 'bg-surface text-success-700' : 'text-white/85 hover:bg-white/10 hover:text-white'}`}
        >
          Invoicing
        </button>
        {!editing && card && (
          <span className="flex min-w-0 basis-full items-center gap-1.5 pb-1 text-xs font-semibold text-white sm:ml-auto sm:basis-auto">
            <span className="shrink-0 rounded-full bg-white/20 px-2 py-0.5">Measuring</span>
            <span className="truncate">{card.itemName}</span>
          </span>
        )}
        {editing && (
          <span className="flex min-w-0 basis-full items-center gap-2 pb-1 text-xs font-semibold text-white sm:ml-auto sm:basis-auto">
            <span className="truncate">Changing: {line.itemName}</span>
            <button className="btn-outline btn-sm shrink-0" onClick={onCancelEdit}><X size={13} /> Stop</button>
          </span>
        )}
      </div>

      <div className="of-step-body p-2 sm:p-3">
        {disabled ? (
          <p className="rounded-md bg-surface-sunken py-8 text-center text-sm text-content-subtle">Choose a customer in the Order Header first.</p>
        ) : (
          // A grid item is `min-width: auto` by default, so a wide table
          // inside one of these columns stretches the column — and with it the
          // whole page — instead of scrolling inside its own box. `min-w-0`
          // lets the column shrink and hands the overflow to the scroller.
          <div className="grid items-start gap-4 lg:grid-cols-2">
          <div className="min-w-0 space-y-2.5">
            {/* Item name needs the room, so Pcs/Rate/Amt only sit beside it
                once there is room for all four. Below that the name takes a
                row of its own and the three numbers share the next one. */}
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-[minmax(0,1fr)_4.5rem_6rem_6.5rem]">
              <div ref={itemFieldRef} className="col-span-3 sm:col-span-1">
                <L>
                  {editing ? 'Add next item' : card ? 'Item — change it here' : 'Item Name'}
                  <a href="/tailoring-items/new" target="_blank" rel="noreferrer" className="ml-1 font-normal text-brand-700 underline">New Item ↗</a>
                </L>
                <SearchableSelect
                  options={catalogue.map((i) => ({
                    id: i.id,
                    label: i.name,
                    sub: gstOn ? `${inr(i.customerRate || 0)} · ${Number(i.taxRate)}% GST` : inr(i.customerRate || 0),
                  }))}
                  value={!editing && !adding && card?.tailoringItemId ? card.tailoringItemId : ''}
                  selectedLabel={card?.itemName}
                  onSelect={pickItem}
                  onSearch={setItemQuery}
                  loading={itemLoading}
                  placeholder={
                    editing ? 'Pick the next garment — it is added at once'
                      : adding ? 'Type the next garment…'
                        : 'Type to search e.g. shirt, pant…'
                  }
                />
              </div>
              <label className="block">
                <L>Pcs</L>
                <input
                  type="number" min={1} className="input text-center font-semibold" value={current.qty} disabled={settled}
                  title={settled ? 'Fixed once worker pay is settled' : undefined}
                  onChange={(e) => patch({ qty: e.target.value })}
                />
              </label>
              <label className="block">
                <L>Rate</L>
                <input type="number" className="input text-right font-semibold" value={current.rate} onChange={(e) => patch({ rate: e.target.value })} />
              </label>
              <div>
                <L>Amt</L>
                {/* Before GST — tax is added once, in Billing. */}
                <input className="input bg-surface-sunken text-right font-bold" title="Pieces × rate, less discount. GST is added in Billing." value={inr(calc.taxableAmt)} readOnly tabIndex={-1} />
              </div>
            </div>

            {/* Picked garments, waiting to be added. They stay here until F5 or
                Save; the highlighted one is the one being measured. */}
            {cards.length > 0 && (
              <div className="rounded-lg border border-dashed border-brand-300 bg-brand-50/40 p-2.5">
                <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2">
                  <span className="text-2xs font-bold uppercase tracking-wider text-brand-700">
                    Picked — not on the order yet
                  </span>
                  <span className="rounded-full bg-brand-600 px-1.5 text-2xs font-bold text-white">{cards.length}</span>
                  <span className="ml-auto text-2xs text-content-subtle">F5 or Save puts them on the order</span>
                </div>

                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {cards.map((c) => {
                    const on = c.localKey === activeCard;
                    const filled = (c.measurements || []).filter((m) => m.value != null).length;
                    const money = computeLine(c);
                    return (
                      <div
                        key={c.localKey}
                        className={`relative rounded-lg border bg-surface p-2 shadow-xs transition-shadow ${on ? 'border-brand-600 ring-2 ring-brand-500/30' : 'border-line hover:shadow-raised'}`}
                      >
                        <button
                          type="button"
                          className="block w-full pr-5 text-left"
                          title={on ? 'This one is open below' : 'Open this one to measure it'}
                          onClick={() => { setActiveCard(c.localKey); setAdding(false); setTab('item'); }}
                        >
                          <span className="flex items-baseline gap-1.5">
                            <b className="truncate text-sm text-content">{c.itemName}</b>
                            <span className="text-2xs text-content-subtle">× {c.qty}</span>
                            <span className="ml-auto text-xs font-semibold tabular-nums text-content-muted">{inr(money.taxableAmt)}</span>
                          </span>
                          <span className="mt-1 flex flex-wrap items-center gap-1">
                            <span className={`rounded-full px-1.5 py-0.5 text-2xs font-semibold ${filled ? 'bg-success-50 text-success-700' : 'bg-warning-50 text-warning-700'}`}>
                              {filled ? `${filled} measured` : 'not measured'}
                            </span>
                            {c.alteration && (
                              <span className="rounded-full bg-warning-50 px-1.5 py-0.5 text-2xs font-semibold text-warning-700">Alteration</span>
                            )}
                            {on && (
                              <span className="rounded-full bg-brand-600 px-1.5 py-0.5 text-2xs font-bold uppercase text-white">Measuring</span>
                            )}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full text-content-subtle hover:bg-danger-50 hover:text-danger-600"
                          title={c.setKey ? `Remove the whole ${c.setName || 'set'}` : 'Remove ' + c.itemName}
                          onClick={() => dropCard(c.localKey)}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })}

                  {/* Start another garment: clears the field and waits. */}
                  <button
                    type="button"
                    onClick={startAnother}
                    title="Add another item"
                    className={`flex min-h-[4.5rem] flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed p-2 text-center transition-colors ${
                      adding
                        ? 'border-brand-600 bg-brand-50 text-brand-700'
                        : 'border-line bg-surface text-content-subtle hover:border-brand-400 hover:text-brand-700'
                    }`}
                  >
                    <Plus size={18} />
                    <span className="text-2xs font-bold uppercase tracking-wider">
                      {adding ? 'Pick it above' : 'Add item'}
                    </span>
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button
                className="of-big flex flex-1 items-center justify-center gap-2 rounded-md border-2 border-success-600 bg-success-50 px-3 py-2 text-sm font-bold uppercase tracking-wide text-success-700 hover:bg-success-600 hover:text-white disabled:opacity-50"
                disabled={!editing && !cards.length}
                onClick={commit}
                title={editing ? 'Already on the order — this just closes it' : 'Put the cards above on the order'}
              >
                <Check size={16} />
                {editing
                  ? ' Done with this item (F5)'
                  : ' Add ' + (cards.length || '') + ' ' + (cards.length === 1 ? 'item' : 'items') + ' to order (F5)'}
              </button>
            </div>
            <p className="text-xs font-semibold text-danger-600">
              Tailoring: {inr(tailoringTotal)} · Alteration: {inr(alterationTotal)}
            </p>

            {tab === 'billing' && (
              <div className="rounded-md border border-line p-2">
                <LiveInvoice form={form} money={money} />
              </div>
            )}

            {/* The order as it stands: its lines, and who is making them. */}
            {leftExtras}
          </div>

          <div className="min-w-0 space-y-2.5">
            {/* measurement grid — all of it, no inner scroll */}
            <div className="rounded-md border border-line p-2">
              {!editing && !card ? (
                <p className="py-6 text-center text-xs italic text-content-subtle">
                  Pick an item above — it appears as a card, and you measure it here.
                </p>
              ) : rows.length === 0 ? (
                <p className="py-6 text-center text-xs text-content-subtle">
                  No measurements set up — add them under Common Measurements.
                </p>
              ) : (
                <div className="grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
                  {rows.map((m) => {
                    if (!m.id) {
                      return (
                        <p
                          key={`z-${m.zone}`}
                          className="col-span-full mt-1 rounded bg-brand-50 px-2 py-0.5 text-2xs font-bold uppercase tracking-wider text-brand-800 first:mt-0"
                        >
                          {ZONE_LABEL[m.zone]}
                        </p>
                      );
                    }
                    const row = rowFor(m.id);
                    const history = historyById.get(m.id);
                    const ph = firstField ? 'e.g. 40//' : '';
                    firstField = false;
                    return (
                      // Name and value stay together on one line; the note
                      // drops below them when there is no room, rather than
                      // being squeezed to a few characters wide on a phone.
                      <div key={m.id} className="of-meas-row flex flex-wrap items-center gap-x-1.5 gap-y-1 border-b border-line/60 py-1 sm:flex-nowrap sm:py-0.5">
                        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-content sm:w-[6.5rem] sm:flex-none" title={m.name}>
                          {m.name}
                        </span>
                        <MeasurementInput
                          className="!w-16 shrink-0 !py-0.5 text-right text-xs font-semibold"
                          placeholder={ph}
                          value={row?.value ?? null}
                          onChange={(value) => setMeasurement(m.id, { value, displayValue: formatMeasurement(value) })}
                        />
                        <input
                          className="input order-last min-w-0 basis-full !py-0.5 text-xs sm:order-none sm:flex-1 sm:basis-auto"
                          placeholder="note"
                          value={row?.notes || ''}
                          onChange={(e) => setMeasurement(m.id, { notes: e.target.value })}
                          aria-label={`${m.name} note`}
                        />
                        <ChangeLogHint compact history={history} onOpen={() => setLogFor({ name: m.name, history })} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="text-2xs text-content-subtle">Fractions: 40/ = 40¼ · 40// = 40½ · 40/// = 40¾ — Enter or Tab moves to the next box.</p>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <label className="flex items-center gap-1.5 font-medium">
                <input type="checkbox" className="text-brand-700" checked={current.alteration} onChange={(e) => patch({ alteration: e.target.checked })} />
                Alteration
              </label>
              <label className="flex items-center gap-1.5 font-medium" title="Show this item's measurements on the printed bill">
                <input type="checkbox" className="text-brand-700" checked={current.printOnInvoice} onChange={(e) => patch({ printOnInvoice: e.target.checked })} />
                Print on Invoice
              </label>
              <button type="button" className="text-sm font-semibold text-brand-700 hover:underline" onClick={() => setImagesOpen(true)}>
                🖼 Add More Images{current.images.length + current.patterns.length + current.handMarks.length > 0 ? ` (${current.images.length + current.patterns.length + current.handMarks.length})` : ''}
              </button>
            </div>

            <label className="block">
              <L>Remark</L>
              <textarea className="input min-h-[2.75rem] text-sm" placeholder="Style notes for this garment…" value={current.remarks} onChange={(e) => patch({ remarks: e.target.value })} />
            </label>

            {/* Rarely used, but it has to be findable — hence the coloured bar. */}
            <div className="rounded-md border border-accent-400">
              <button
                type="button"
                onClick={() => setMoreOpen((o) => !o)}
                aria-expanded={moreOpen}
                className={`flex w-full items-center gap-2 bg-accent-600 px-2.5 py-1.5 text-left text-xs font-bold uppercase tracking-wider text-white hover:brightness-110 ${
                  moreOpen ? 'rounded-t-md' : 'rounded-md'
                }`}
              >
                <span className={`inline-block transition-transform ${moreOpen ? 'rotate-90' : ''}`}>▸</span>
                More options
                <span className="truncate font-normal normal-case tracking-normal text-white/80">— discount, salesman, voice note</span>
              </button>
              {moreOpen && (
                <div className="space-y-3 border-t border-line p-2.5">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <label className="block">
                <L>Discount %</L>
                <input type="number" className="input" value={current.discountPct} onChange={(e) => patch({ discountPct: e.target.value, discountAmt: '' })} />
              </label>
              <label className="block">
                <L>Discount ₹</L>
                <input type="number" className="input" value={current.discountAmt ?? ''} onChange={(e) => patch({ discountAmt: e.target.value, discountPct: 0 })} />
              </label>
              {gstOn && (
                <>
                  <label className="block">
                    <L>GST %</L>
                    <select className="input" value={current.taxRate} onChange={(e) => patch({ taxRate: e.target.value })}>
                      <option value="">—</option>
                      {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}
                    </select>
                  </label>
                  <label className="flex items-end gap-1.5 pb-2 text-sm font-medium">
                    <input type="checkbox" className="text-brand-700" checked={current.gstIncl} onChange={(e) => patch({ gstIncl: e.target.checked })} />
                    Rate includes GST
                  </label>
                </>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 rounded-md bg-surface-sunken p-2 text-center text-xs">
              <div><p className="text-content-subtle">Taxable</p><p className="font-bold text-content">{inr(calc.taxableAmt)}</p></div>
              <div><p className="text-content-subtle">GST</p><p className="font-bold text-content">{inr(calc.gstAmount)}</p></div>
              <div><p className="text-content-subtle">Final</p><p className="font-bold text-brand-800">{inr(calc.finalAmt)}</p></div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <L>Salesman (this item)</L>
                <SearchableSelect
                  options={salesmen.map((s) => ({ id: s.id, label: s.name, sub: s.contact || '' }))}
                  value={current.salesmanId}
                  onSelect={(o) => patch({ salesmanId: String(o.id), salesmanName: o.label })}
                  placeholder={salesmen.length ? 'Pick a salesman…' : 'None added yet — Salesmen menu'}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <L className="!mb-0">Voice note</L>
              <AudioNote audio={current.audio} onChange={(audio) => patch({ audio })} error={error} />
            </div>
                </div>
              )}
            </div>

          </div>
          </div>
        )}
      </div>

      {logFor && (
        <ChangeLogModal
          title={`${logFor.name} — change log`}
          hint="From the customer's saved measurements, not this order."
          history={logFor.history}
          onClose={() => setLogFor(null)}
        />
      )}
      <ImagesModal
        open={imagesOpen}
        onClose={() => setImagesOpen(false)}
        draft={current}
        patch={patch}
      />
    </section>
  );
}

// The bill as it stands right now — updates as items, charges and payments
// change, before anything is saved. Laid out like the printed invoice.
function LiveInvoice({ form, money }) {
  const { currentCompany } = useAuth();
  const nav = useNavigate();
  const { totals, billAmount, received, due, salesmen } = money;
  const { data: measData } = useQuery({ queryKey: ['measurements', ''], queryFn: () => listMeasurements('') });
  const metricName = (m) => m.name || (measData?.measurements || []).find((x) => x.id === Number(m.measurementId))?.name || '';
  const adjust = Number(form.adjustAmount) || 0;
  const freight = Number(form.freightAmount) || 0;
  const dmy = (v) => fmtDMY(v) || '—';
  const when = (dt) => (dt ? `${dmy(dt)} ${dt.slice(11, 16)}` : '—');
  const smName = (sid) => salesmen.find((x) => String(x.id) === String(sid))?.name;
  const people = [smName(form.salesman1Id), smName(form.salesman2Id)].filter(Boolean).join(', ');
  const row = (label, value, cls = '') => (
    <div className={`flex justify-between gap-4 ${cls}`}><span>{label}</span><span className="tabular-nums">{value}</span></div>
  );

  return (
    <div className="doc-sheet overflow-hidden rounded-md border border-line text-content">
      <div className="flex flex-wrap items-start justify-between gap-2 px-3 py-2 text-white sm:gap-3" style={{ backgroundColor: '#10616f' }}>
        <div className="min-w-0">
          <p className="truncate text-base font-bold">{currentCompany?.name || 'Invoice'}</p>
          <p className="truncate text-2xs text-white/75">
            {[currentCompany?.address, currentCompany?.phone && `Ph: ${currentCompany.phone}`, currentCompany?.gstin && `GSTIN: ${currentCompany.gstin}`].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="shrink-0 rounded bg-white px-2 py-1 text-right" style={{ color: '#15424d' }}>
          <p className="text-2xs font-bold uppercase tracking-wider" style={{ color: '#1996a8' }}>Tax Invoice</p>
          <p className="text-sm font-bold">#{form.orderNo || 'New'}</p>
          <p className="text-2xs">Date: {dmy(form.orderDate)}</p>
        </div>
      </div>

      <div className="max-h-[30rem] overflow-x-auto overflow-y-auto px-3 py-2.5">
        <div className="grid gap-3 text-xs sm:grid-cols-2">
          <div>
            <p className="text-2xs font-bold uppercase tracking-wider text-content-subtle">Billed To</p>
            <p className="font-semibold">{form.customerName || '—'}</p>
            {form.forFamily && form.familyMemberName && (
              <p className="font-semibold text-brand-700">
                {forWhom({ name: form.familyMemberName, relation: form.familyMemberRelation })}
              </p>
            )}
            <p className="text-content-muted">{form.customerMobile}</p>
            <p className="text-content-muted">{[form.customerAddress, form.customerPlace].filter(Boolean).join(', ')}</p>
          </div>
          <div>
            <p className="text-2xs font-bold uppercase tracking-wider text-content-subtle">Order Details</p>
            <p className="text-content-muted">Ref: {form.reference || '—'}</p>
            <p className="text-content-muted">Delivery: {when(form.deliveryDate)}</p>
            {form.trialOn && <p className="text-content-muted">Trial: {when(form.trialDate)}</p>}
            {people && <p className="text-content-muted">Salesman: {people}</p>}
          </div>
        </div>

        <table className="mt-2.5 w-full min-w-[30rem] text-xs">
          <thead>
            <tr className="bg-surface-sunken text-left text-2xs uppercase tracking-wider text-content-muted">
              <th className="px-1.5 py-1">Sr</th>
              <th className="px-1.5 py-1">Item</th>
              <th className="px-1.5 py-1 text-center">Qty</th>
              <th className="px-1.5 py-1 text-right">Rate</th>
              <th className="px-1.5 py-1 text-right">Disc</th>
              <th className="px-1.5 py-1 text-right">GST</th>
              <th className="px-1.5 py-1 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {form.items.length === 0 ? (
              <tr><td colSpan={7} className="px-1.5 py-4 text-center italic text-content-subtle">No items yet</td></tr>
            ) : groupBillLines(form.items).map((it, i) => (
              <tr key={it.localKey} className="border-b border-line align-top">
                <td className="px-1.5 py-1 text-content-subtle">{i + 1}</td>
                <td className="px-1.5 py-1">
                  <p className="font-medium">{it.itemName}{it.alteration && <span className="ml-1 text-warning-700">(Alteration)</span>}</p>
                  {it.printOnInvoice !== false && (it.measurements || []).some((m) => m.value != null) && (
                    <p className="text-2xs text-content-subtle">
                      {(it.measurements || []).filter((m) => m.value != null).map((m) => `${metricName(m)} ${formatMeasurement(m.value)}`.trim()).join(' · ')}
                    </p>
                  )}
                </td>
                <td className="px-1.5 py-1 text-center">{it.qty}</td>
                <td className="px-1.5 py-1 text-right tabular-nums">{inr(it.rate)}</td>
                <td className="px-1.5 py-1 text-right tabular-nums">{inr(it.discountAmt || 0)}</td>
                <td className="px-1.5 py-1 text-right tabular-nums">{inr(it.gstAmount || 0)}</td>
                <td className="px-1.5 py-1 text-right font-semibold tabular-nums">{inr(it.finalAmt)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="ml-auto mt-2 w-full max-w-[16rem] space-y-0.5 text-xs">
          {row('Subtotal', inr(totals.subtotal), 'text-content-muted')}
          {totals.discountTotal > 0 && row('Discount', `− ${inr(totals.discountTotal)}`, 'text-success-600')}
          {totals.gstTotal > 0 && row('CGST', inr(totals.gstTotal / 2), 'text-content-muted')}
          {totals.gstTotal > 0 && row('SGST', inr(totals.gstTotal / 2), 'text-content-muted')}
          {freight !== 0 && row('Freight', inr(freight), 'text-content-muted')}
          {adjust !== 0 && row('Adjustment', inr(adjust), 'text-content-muted')}
          <div className="mt-1 flex justify-between rounded px-2 py-1 font-bold text-white" style={{ backgroundColor: '#10616f' }}>
            <span>Grand Total</span><span className="tabular-nums">{inr(billAmount)}</span>
          </div>
          {row('Received', inr(received), 'pt-1 text-success-600')}
          {row('Balance Due', inr(due), 'font-bold text-danger-600')}
        </div>

        {form.metersUsed !== '' && form.metersUsed != null && (
          <p className="mt-1 text-2xs text-content-subtle">Meters used: {form.metersUsed}</p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line bg-surface-sunken px-3 py-1.5 text-2xs text-content-subtle">
        <span>Live preview — Save the order to print.</span>
        {form.orderNo && (
          <button type="button" className="btn-outline btn-sm" onClick={() => {
            const id = window.location.pathname.match(/invoices\/(\d+)/)?.[1];
            if (id) nav(`/invoices/${id}`);
          }}>
            Print saved copy
          </button>
        )}
      </div>
    </div>
  );
}

// Photos of the garment plus pattern / hand-mark images, in one place.
function ImagesModal({ open, onClose, draft, patch }) {
  const { success, error } = useToast();
  const addImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { const r = await uploadFile(f); patch({ images: [...draft.images, r.url] }); } catch (x) { error(x.response?.data?.message || 'Upload failed'); }
    e.target.value = '';
  };
  return (
    <>
      <Modal open={open} onClose={onClose} title="Images" size="lg">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-content-muted">Garment photos</span>
          <label className="btn-outline btn-sm">📷 Camera<input type="file" accept="image/*" capture="environment" hidden onChange={addImage} /></label>
          <label className="btn-outline btn-sm">⬆ Upload<input type="file" accept="image/*" hidden onChange={addImage} /></label>
          {draft.images.map((img, i) => (
            <span key={i} className="relative">
              <img src={assetUrl(img)} alt="" className="h-14 w-14 rounded-md border border-line object-cover" />
              <button className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-danger-500 text-[10px] text-white" onClick={() => patch({ images: draft.images.filter((_, j) => j !== i) })}>×</button>
            </span>
          ))}
        </div>
        <ManageImagesBody
          patterns={draft.patterns}
          handMarks={draft.handMarks}
          onChange={(patterns, handMarks) => patch({ patterns, handMarks })}
          error={error}
          success={success}
        />
      </Modal>
    </>
  );
}

/* ---------------- BILLING ---------------- */
function Billing({ form, set, salesmen, totals, billAmount, received, due }) {
  const gstOn = useGstOn();
  const [pay, setPay] = useState({ date: todayISO(), amount: '', type: 'CASH', remark: '' });
  const cgst = Math.round((totals.gstTotal / 2) * 100) / 100;
  const sgst = Math.round((totals.gstTotal - cgst) * 100) / 100;
  const ro = 'input bg-surface-sunken text-right tabular-nums';

  const record = () => {
    const amount = Number(pay.amount);
    if (!(amount > 0)) return;
    set({ payments: [...form.payments, { localKey: k(), ...pay, amount }] });
    setPay({ date: todayISO(), amount: '', type: 'CASH', remark: '' });
  };
  const removePay = (key) => set({ payments: form.payments.filter((p) => p.localKey !== key) });

  return (
    <div className="space-y-3">
      <div className="of-when grid grid-cols-2 gap-x-3 gap-y-2 md:grid-cols-4">
        <label className="block"><L>Item Total</L><input className={ro} value={totals.subtotal} readOnly tabIndex={-1} /></label>
        <label className="block"><L>Discount</L><input className={ro} value={totals.discountTotal} readOnly tabIndex={-1} /></label>
        <label className="block"><L>Taxable Amt</L><input className={ro} value={Math.round((totals.subtotal - totals.discountTotal) * 100) / 100} readOnly tabIndex={-1} /></label>
        <label className="block"><L>Meters Used</L><input type="number" step="0.01" className="input text-right" value={form.metersUsed} onChange={(e) => set({ metersUsed: e.target.value })} /></label>

        {gstOn && <label className="block"><L>CGST ₹</L><input className={ro} value={cgst} readOnly tabIndex={-1} /></label>}
        {gstOn && <label className="block"><L>SGST ₹</L><input className={ro} value={sgst} readOnly tabIndex={-1} /></label>}
        <label className="block"><L>Adjust (+/−)</L><input type="number" step="0.01" className="input text-right" value={form.adjustAmount} placeholder="0" onChange={(e) => set({ adjustAmount: e.target.value })} /></label>
        <label className="block"><L>Freight</L><input type="number" step="0.01" min={0} className="input text-right" value={form.freightAmount} placeholder="0" onChange={(e) => set({ freightAmount: e.target.value })} /></label>

        <label className="block">
          <L>Salesman 1</L>
          <select className="input" value={form.salesman1Id} onChange={(e) => set({ salesman1Id: e.target.value })}>
            <option value="">—</option>
            {salesmen.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label className="block">
          <L>Salesman 2</L>
          <select className="input" value={form.salesman2Id} onChange={(e) => set({ salesman2Id: e.target.value })}>
            <option value="">—</option>
            {salesmen.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label className="col-span-2 block">
          <L>Bill Amount</L>
          <input className={`${ro} font-bold text-brand-900`} value={inr(billAmount)} readOnly tabIndex={-1} />
        </label>
      </div>

      {/* The receipt strip is one row on a desk machine and wraps into a
          stack on a phone, so the boxes grow rather than keeping desk widths
          that would leave a single field stranded per line. */}
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface-sunken px-3 py-2">
        <p className={`w-full text-lg font-bold tabular-nums sm:mr-auto sm:w-auto ${due > 0 ? 'text-danger-600' : 'text-success-700'}`}>
          Total Due: {inr(due)}
          <span className="ml-3 text-xs font-medium text-content-muted">Received {inr(received)}</span>
        </p>
        <span className="w-full text-xs font-semibold text-content-muted sm:w-auto">Advance / Receipt:</span>
        <input type="date" className="input !w-auto min-w-[8.5rem] flex-1 sm:!w-36 sm:flex-none" value={pay.date} onChange={(e) => setPay((p) => ({ ...p, date: e.target.value }))} />
        <input
          type="number" className="input !w-auto min-w-[6rem] flex-1 text-right sm:!w-28 sm:flex-none" placeholder="Amount" value={pay.amount}
          onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter') record(); }}
        />
        <select className="input !w-auto min-w-[5.5rem] flex-1 sm:!w-24 sm:flex-none" value={pay.type} onChange={(e) => setPay((p) => ({ ...p, type: e.target.value }))}>
          {PAY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input className="input !w-auto min-w-[8rem] flex-1 sm:!w-40 sm:flex-none" placeholder="Narration" value={pay.remark} onChange={(e) => setPay((p) => ({ ...p, remark: e.target.value }))} />
        {due > 0 && (
          <button className="btn-outline flex-1 sm:flex-none" onClick={() => setPay((p) => ({ ...p, amount: String(due) }))} title="Fill the full balance">Full</button>
        )}
        <button className="btn-primary flex-1 sm:flex-none" disabled={!(Number(pay.amount) > 0)} onClick={record}>Record</button>
      </div>

      {form.payments.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[28rem] text-xs">
            <thead className="bg-surface-sunken text-left text-2xs font-bold uppercase text-content-muted">
              <tr><th className="px-2 py-1">Date</th><th className="px-2 py-1">Mode</th><th className="px-2 py-1">Narration</th><th className="px-2 py-1 text-right">Amount</th><th className="w-10" /></tr>
            </thead>
            <tbody>
              {form.payments.map((p) => (
                <tr key={p.localKey} className="border-t border-line">
                  <td className="px-2 py-1">{fmtDMY(p.date) || p.date}</td>
                  <td className="px-2 py-1">{PAY_TYPES.find(([v]) => v === p.type)?.[1] || p.type}</td>
                  <td className="px-2 py-1 text-content-muted">{p.remark}</td>
                  <td className="px-2 py-1 text-right font-semibold tabular-nums">{inr(p.amount)}</td>
                  <td className="px-1 text-right">
                    <button className="btn-icon hover:bg-danger-50 hover:text-danger-600" title="Remove" onClick={() => removePay(p.localKey)}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- AUDIO NOTE ---------------- */
function AudioNote({ audio, onChange, error }) {
  const [recording, setRecording] = useState(false);
  const [recErr, setRecErr] = useState('');
  const [mediaRec, setMediaRec] = useState(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: rec.mimeType });
        const url = URL.createObjectURL(blob);
        onChange(url);
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      setMediaRec(rec);
      setRecording(true);
      setRecErr('');
    } catch {
      setRecErr('Could not access microphone — permission denied or unavailable.');
    }
  };

  const stop = () => {
    if (mediaRec) mediaRec.stop();
    setRecording(false);
  };

  return (
    <div className="flex items-center gap-2">
      {recording ? (
        <button className="btn-danger !px-2.5 !py-1.5 text-xs" onClick={stop}>● Stop</button>
      ) : (
        <button className="btn-outline !px-2.5 !py-1.5 text-xs" onClick={start}>🎙 Record</button>
      )}
      <label className="btn-outline !px-2.5 !py-1.5 text-xs">⬆ Upload
        <input type="file" accept="audio/*" hidden onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          try { const r = await uploadFile(f); onChange(r.url); } catch (x) { error(x.response?.data?.message || 'Upload failed'); }
          e.target.value = '';
        }} />
      </label>
      {audio && (
        <a href={audio} className="text-xs font-semibold text-brand-700 hover:underline" onClick={(e) => e.preventDefault()}>🎵 note</a>
      )}
      {recErr && <span className="text-xs text-danger-600">{recErr}</span>}
    </div>
  );
}

/* ---------------- MANAGE ADDITIONAL IMAGES (Pattern / Hand Mark) ---------------- */
function ManageImagesBody({ patterns, handMarks, onChange, error, success }) {
  const [p, setP] = useState({ remark: '', image: '' });
  const [h, setH] = useState({ remark: '', image: '' });

  const pickImage = async (e, which) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { const r = await uploadFile(f); which === 'p' ? setP((x) => ({ ...x, image: r.url })) : setH((x) => ({ ...x, image: r.url })); } catch (x) { error(x.response?.data?.message || 'Upload failed'); }
    e.target.value = '';
  };

  const savePattern = () => { if (!p.image) return error('Upload a pattern image first.'); onChange([...patterns, { image: p.image, remark: p.remark }], handMarks); setP({ remark: '', image: '' }); success('Pattern remark saved.'); };
  const saveHandMark = () => { if (!h.image) return error('Upload a hand mark image first.'); onChange(patterns, [...handMarks, { image: h.image, remark: h.remark }]); setH({ remark: '', image: '' }); success('Hand mark remark saved.'); };

  return (
    <>
      <p className="mb-3 rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700">Images will be uploaded automatically after you save the invoice.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <section className="rounded-xl border border-line p-3">
          <h4 className="mb-2 text-sm font-bold text-brand-900">Pattern</h4>
          <div className="mb-2 flex items-center gap-2">
            {p.image ? <img src={assetUrl(p.image)} alt="" className="h-16 w-16 rounded-md border object-cover" /> : <span className="flex h-16 w-16 items-center justify-center rounded-md border-2 border-dashed text-content-subtle">🖼</span>}
            <label className="btn-outline !px-2.5 !py-1.5 text-xs">Upload<input type="file" accept="image/*" hidden onChange={(e) => pickImage(e, 'p')} /></label>
          </div>
          <textarea className="input min-h-[64px] text-xs" placeholder="Pattern Remark" value={p.remark} onChange={(e) => setP((x) => ({ ...x, remark: e.target.value }))} />
          <button className="btn-primary mt-2 w-full !py-1.5 text-xs" onClick={savePattern}>Save Remark</button>
          {patterns.length > 0 && (
            <ul className="mt-2 space-y-1">
              {patterns.map((x, i) => <li key={i} className="flex items-center gap-2 text-xs text-content-muted"><img src={assetUrl(x.image)} className="h-6 w-6 rounded object-cover" />{x.remark || 'Pattern image'}</li>)}
            </ul>
          )}
        </section>

        <section className="rounded-xl border border-line p-3">
          <h4 className="mb-2 text-sm font-bold text-brand-900">Hand Mark</h4>
          <div className="mb-2 flex items-center gap-2">
            {h.image ? <img src={assetUrl(h.image)} alt="" className="h-16 w-16 rounded-md border object-cover" /> : <span className="flex h-16 w-16 items-center justify-center rounded-md border-2 border-dashed text-content-subtle">🖼</span>}
            <label className="btn-outline !px-2.5 !py-1.5 text-xs">Upload<input type="file" accept="image/*" hidden onChange={(e) => pickImage(e, 'h')} /></label>
          </div>
          <textarea className="input min-h-[64px] text-xs" placeholder="Hand Mark Remark" value={h.remark} onChange={(e) => setH((x) => ({ ...x, remark: e.target.value }))} />
          <button className="btn-primary mt-2 w-full !py-1.5 text-xs" onClick={saveHandMark}>Save Remark</button>
          {handMarks.length > 0 && (
            <ul className="mt-2 space-y-1">
              {handMarks.map((x, i) => <li key={i} className="flex items-center gap-2 text-xs text-content-muted"><img src={assetUrl(x.image)} className="h-6 w-6 rounded object-cover" />{x.remark || 'Hand mark image'}</li>)}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}

/* ---------------- QUICK ADD CUSTOMER ---------------- */
// Opened by pressing Enter on a customer search with no match. Whatever was
// typed is carried in: a 10-digit number becomes the mobile, anything else the name.
export function QuickAddCustomer({ open, onClose, onCreated, initial = '' }) {
  const { success, error } = useToast();
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const isPhone = /^\d{10}$/.test(initial.trim());
  useEffect(() => {
    if (!open) return;
    setName(isPhone ? '' : initial);
    setMobile(isPhone ? initial.trim() : '');
    setEmail('');
  }, [open, initial, isPhone]);
  const onEnter = (e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } };

  const save = async () => {
    if (!name) return error('Name is required.');
    if (!/^\d{10}$/.test(mobile)) return error('Mobile number must contain exactly 10 digits.');
    try {
      const res = await createCustomer({ name, mobile, email, country: 'India' });
      success('Customer created successfully');
      onCreated(res.customer);
      setName(''); setMobile(''); setEmail('');
    } catch (x) {
      error(x.response?.data?.message || 'Could not create customer.');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="New Customer — press Enter to save" size="sm"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={save}>Save Customer</button>
      </>}>
      <div className="space-y-3">
        <Field label="Customer Name" required>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={onEnter} autoFocus={isPhone || !initial} />
        </Field>
        <Field label="Mobile" required hint="Exactly 10 digits — then press Enter">
          <input
            className="input" inputMode="numeric" maxLength={10} value={mobile}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
            onKeyDown={onEnter}
            autoFocus={!!initial && !isPhone}
          />
        </Field>
        <Field label="Email"><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={onEnter} /></Field>
      </div>
    </Modal>
  );
}

